/** Small pieces the requirements views share: keys, priority, severity, quality marks, roll-up bars, the AI caption, tags. */
import { Circle, CircleAlert, CircleMinus, ShieldAlert, SignalHigh, SignalLow, SignalMedium, Sparkles, TriangleAlert } from '@orgus/ui/icons';
import type { ComponentType } from 'react';
import { ITEM_PRIORITY_LABEL, QUALITY_LABEL, SEVERITY_LABEL, itemProgress } from '@orgus/protocol';
import type { BacklogItem, ItemPriority, QualityIssue, Severity } from '@orgus/protocol';
import { MeterBar, Tooltip, cn, formatInt } from '@orgus/ui';

type Icon = ComponentType<{ className?: string }>;

/** The human key in mono: QAWL-12. */
export function KeyLabel({ value, className }: { value: string; className?: string }) {
  if (!value) return null;
  return <span className={cn('shrink-0 font-mono text-caption text-fg-subtle num', className)}>{value}</span>;
}

const LEVEL_ICON: Record<Exclude<ItemPriority, 0>, Icon> = { 1: SignalHigh, 2: SignalMedium, 3: SignalLow };

/**
 * Priority as signal bars that keep their shape at every level: the unlit bars are drawn quiet,
 * so Low never shrinks to a lone speck. Urgent has its own shape. Without the word it carries
 * "Priority: Low" as its tooltip and accessible name.
 */
export function PriorityLevel({ priority, withLabel, className }: { priority: ItemPriority; withLabel?: boolean; className?: string }) {
  const label = ITEM_PRIORITY_LABEL[priority];
  const I = priority === 0 ? null : LEVEL_ICON[priority];
  const mark = (
    <span role="img" aria-label={`Priority: ${label}`} tabIndex={withLabel ? undefined : -1} className={cn('inline-flex shrink-0 items-center gap-2 text-ui', className)}>
      {I ? (
        <span className="relative inline-flex size-4 shrink-0" aria-hidden>
          <SignalHigh className="absolute inset-0 size-4 text-border-strong" />
          <I className="relative size-4 text-fg-muted" />
        </span>
      ) : (
        <CircleAlert className="size-4 shrink-0 text-fg" aria-hidden />
      )}
      {withLabel && <span className="text-fg-muted">{label}</span>}
    </span>
  );
  return withLabel ? mark : <Tooltip content={`Priority: ${label}`}>{mark}</Tooltip>;
}

const SEVERITY_ICON: Record<Severity, Icon> = { critical: ShieldAlert, major: CircleAlert, minor: CircleMinus, trivial: Circle };

/** Severity of a defect: icon plus label. Only critical is red; the rest are told apart by shape. */
export function SeverityMark({ severity, label = true, className }: { severity: Severity; label?: boolean; className?: string }) {
  const I = SEVERITY_ICON[severity];
  const critical = severity === 'critical';
  const mark = (
    <span className={cn('inline-flex shrink-0 items-center gap-2 text-ui', className)} role={label ? undefined : 'img'} aria-label={label ? undefined : `${SEVERITY_LABEL[severity]} severity`}>
      <span className={cn('inline-flex', critical ? 'text-status-danger' : 'text-fg-subtle')}>
        <I className="size-4" />
      </span>
      {label && <span className={critical ? 'font-medium text-status-danger' : 'text-fg-muted'}>{SEVERITY_LABEL[severity]}</span>}
    </span>
  );
  return label ? mark : <Tooltip content={`${SEVERITY_LABEL[severity]} severity`}>{mark}</Tooltip>;
}

/** What a quality finding list says in one line, for a tooltip or a caption. */
export function qualitySummary(issues: readonly QualityIssue[]): string {
  return issues.length === 1 ? QUALITY_LABEL[issues[0]!.code] : `${issues.length} things to fix: ${[...new Set(issues.map((i) => QUALITY_LABEL[i.code].toLowerCase()))].join(', ')}`;
}

/** A quiet quality mark: the findings in the tooltip. Grey: quality is a to-do, not an alarm. */
export function QualityMark({ issues, className }: { issues: readonly QualityIssue[] | undefined; className?: string }) {
  if (!issues?.length) return null;
  const label = qualitySummary(issues);
  return (
    <Tooltip content={label}>
      <span role="img" aria-label={label} tabIndex={0} className={cn('inline-flex shrink-0 text-fg-subtle', className)}>
        <TriangleAlert className="size-4" />
      </span>
    </Tooltip>
  );
}

/**
 * Roll-up progress of a parent: items done of total ("4 of 9"), the same number and meaning in
 * the list, the board and the drawer, with a thin neutral meter beside it once anything is done
 * (an empty meter at zero reads as broken). Points, when estimated, are in the tooltip.
 */
export function Rollup({ item, items, className, wide, direct }: { item: BacklogItem; items: readonly BacklogItem[]; className?: string; wide?: boolean; /** Count the items directly under it (the set a list of its children shows); the full roll-up goes in the tooltip. */ direct?: boolean }) {
  const all = itemProgress(item, items);
  const kids = direct ? items.filter((i) => i.parentId === item.id) : [];
  const p = direct ? { done: kids.filter((i) => i.status === 'done').length, total: kids.length, points: all.points } : all;
  if (p.total <= 1 && !items.some((i) => i.parentId === item.id)) return null;
  const text = `${formatInt(p.done)} of ${formatInt(p.total)}`;
  const share = p.total ? p.done / p.total : 0;
  const nested = direct && all.total !== p.total ? `; ${formatInt(all.done)} of ${formatInt(all.total)} counting everything under them` : '';
  const tip = `${text} done${nested}${p.points.total ? `, ${formatInt(p.points.done)} of ${formatInt(p.points.total)} points` : ''}`;
  return (
    <Tooltip content={tip}>
      <span className={cn('inline-flex shrink-0 items-center gap-2', className)} tabIndex={0} aria-label={`Progress: ${tip}`}>
        <span className={cn('flex', wide ? 'w-24' : 'w-12')} aria-hidden>
          {p.done > 0 && <MeterBar value={share} label={`Progress: ${tip}`} className="w-full" />}
        </span>
        {/* Wide (a section header): the figure names what it counts, "1 of 3 done". */}
        <span className="min-w-12 text-right text-caption text-fg-muted num">{wide ? `${text} done` : text}</span>
      </span>
    </Tooltip>
  );
}

/** The words of a roll-up, for a meta line: "3 of 8 done". */
export function rollupText(item: BacklogItem, items: readonly BacklogItem[]): string | null {
  const p = itemProgress(item, items);
  if (p.total <= 1 && !items.some((i) => i.parentId === item.id)) return null;
  return `${formatInt(p.done)} of ${formatInt(p.total)} done`;
}

/** "Drafted by AI": the caption an AI-written item carries until a person rewrites it. */
export function AiCaption({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 text-caption text-fg-subtle', className)}>
      <Sparkles className="size-3" aria-hidden />
      Drafted by AI
    </span>
  );
}

/** A small neutral tag: a label or a component. */
export function Tag({ children, className }: { children: string; className?: string }) {
  return (
    <span data-chip className={cn('inline-flex h-5 max-w-32 shrink-0 items-center truncate rounded-control bg-hover px-2 text-caption text-fg-muted', className)}>
      {children}
    </span>
  );
}

/** The first tag, then "+N" with the rest in a tooltip: one chip per row. */
export function Tags({ values, className }: { values: readonly string[]; className?: string }) {
  if (!values.length) return null;
  const rest = values.slice(1);
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1', className)}>
      <Tag>{values[0]!}</Tag>
      {rest.length > 0 && (
        <Tooltip content={rest.join(', ')}>
          <span tabIndex={0} className="text-caption text-fg-subtle num">
            +{rest.length}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

/** Every tag, for a drawer where there is room. */
export function TagList({ values, className }: { values: readonly string[]; className?: string }) {
  if (!values.length) return null;
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)} data-chip-group>
      {values.map((v) => (
        <Tag key={v}>{v}</Tag>
      ))}
    </span>
  );
}
